import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { Guid } from "@/Common/Models/Guid";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { IconButton } from "@/DesignSystem/Molecules/IconButton";

import styles from "./ProviderSelection.module.scss";

import XRegularIcon from "@/DesignSystem/Atoms/Icons/Types/X/x-regular.svg";

function getEmployees(): Immutable.List<SearchableItemModelT<Person>> {
    if (cacheStore.IsChristus()) {
        return cacheStore.EmployeeCache
            .filter(p => p.Type === EmployeeType.Practician)
            .filter(p => !IsNullOrWhiteSpace(p.LicenceDSQ) && Employee.getIssuingOrgRule(p))
            .map(p => {
                const person = Person.FromEmployee(p);
                const licenseDsq = IsNullOrWhiteSpace(p.LicenceDSQ) ? "" : ` (${p.LicenceDSQ})`;

                return new SearchableItemModelT<Person>(
                    `${person.NameWithPrefix}${licenseDsq}`,
                    person.ContactId,
                    person
                );
            })
            .toImmutable();
    }

    return cacheStore.GetCliniciansWithClinicalEmployeeType()
        .filter(person => !person.isSupervisorRequired)
        .map(person => {
            const license = IsNullOrWhiteSpace(person.Licence) ? "" : ` (${person.Licence})`;

            return new SearchableItemModelT<Person>(
                `${person.NameWithPrefix}${license}`,
                person.ContactId,
                person
            );
        })
        .toImmutable();
}

function ProviderSelect(props: Readonly<{ contactFacetId: Guid, onChange: (contactFacetId: Guid) => void }>): JSX.Element {
    if (GuidHelper.IsNullOrEmpty(props.contactFacetId)) {
        return (
            <TextboxControl
                autoCompleteItems={getEmployees()}
                onAutoCompleteSelectCallBack={(_: any, provider: Person) => props.onChange(provider.ContactFacetId)}
                onAutoCompleteSelectClearTextBox={true}
                dataCy="ontarioCommonDataSetExportForm_ProviderSelect"
            />
        );
    }

    const person = cacheStore.GetCliniciansPersonsByContactFacetId(props.contactFacetId);
    return (
        <div className={styles.selectedProviderSection}>
            <span style={{ marginRight: "8px" }}>{person.NameWithPrefix}</span>
            <IconButton
                variant="error"
                category="tertiary"
                size="sm"
                onClick={() => props.onChange(null)}
                data-cy="ontarioCommonDataSetExportForm_DeselectProviderButton"
            >
                <XRegularIcon />
            </IconButton>
        </div>
    );
}

function ProviderSelection(props: Readonly<{ providerContactFacetId: Guid, onChange: (contactFacetId: Guid) => void }>): JSX.Element {
    const { getText } = useTranslation();

    return (
        <div className={styles.provider}>
            <span>{getText("ProviderLabel")}<span className={styles.required}>*</span></span>
            <ProviderSelect
                contactFacetId={props.providerContactFacetId}
                onChange={providerContactFacetId => props.onChange(providerContactFacetId)}
            />
        </div>
    );
}

export { ProviderSelection };
